import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  MoreVertical,
  Calendar,
  Clock,
  User,
  Filter,
  ArrowRightLeft,
  Users,
  CheckCircle2,
  AlertCircle,
  XCircle,
  MapPin,
  Pencil,
  Trash2,
  ShieldAlert,
  SlidersHorizontal as Filters,
  X,
  Clock9,
  Eye,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './Attendance.module.css';
import Pagination from '../../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../../components/Common/Select/PaginatedSelect';
import Footer from '../../../components/Common/Footer/Footer';
import NoData from '../../../components/Common/NoData/NoData';
import { useLoading } from '../../../context/LoadingContext';
import AttendanceDrawer from './AttendanceDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const Attendance = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const [filters, setFilters] = useState({
    status: null,
    department: null,
    date: ''
  });

  const [isSummaryOpen, setIsSummaryOpen] = useState(true);

  const [summary, setSummary] = useState({
    total_employees: 0,
    present_today: 0,
    late_checkin: 0,
    absent: 0
  });

  const metrics = [
    {
      label: 'Total Employees',
      value: (summary?.total_employees || 0).toString(),
      icon: <Users size={22} />,
      theme: 'orange',
      highlight: true
    },
    {
      label: 'Present Today',
      value: (summary?.present_today || 0).toString(),
      icon: <CheckCircle2 size={22} />,
      theme: 'green'
    },
    {
      label: 'Late Check-in',
      value: (summary?.late_checkin || 0).toString(),
      icon: <Clock size={22} />,
      theme: 'blue'
    },
    {
      label: 'Absent',
      value: (summary?.absent || 0).toString(),
      icon: <XCircle size={22} />,
      theme: 'purple'
    }
  ];

  const statusOptions = [
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FULL_DAY', label: 'Full Day' },
    { value: 'HALF_DAY', label: 'Half Day' },
    { value: 'FIRST_HALF', label: 'First Half' },
    { value: 'SECOND_HALF', label: 'Second Half' },
    { value: 'ABSENT', label: 'Absent' },
    { value: 'HOLIDAY', label: 'Holiday' },
    { value: 'HOLIDAY_WORKING', label: 'Holiday Working' },
    { value: 'LEAVE', label: 'Leave' },
    { value: 'LEAVE_WORKING', label: 'Leave Working' },
    { value: 'PENDING_ADMIN_APPROVAL', label: 'Pending Approval' }
  ];

  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  });

  const fetchAttendance = async () => {
    try {
      const endpoint = activeTab === 'pending' ? '/attendance/pending-approval' : '/visit-sessions';
      const response = await api.get(endpoint, {
        params: {
          page: pagination.current_page,
          per_page: pagination.per_page,
          search: searchTerm,
          status: filters.status?.value,
          employee_id: filters.employee?.value,
          project_id: filters.project?.value,
          date: filters.date
        }
      });
      if (response.data) {
        const resData = response.data;
        if (resData.success && resData.data) {
          setAttendance(resData.data.data || []);
          setPagination(prev => ({
            ...prev,
            total: resData.data.total || 0,
            last_page: resData.data.last_page || 1
          }));
        } else {
          setAttendance(resData.data || []);
          setPagination(prev => ({
            ...prev,
            total: resData.total || 0,
            last_page: resData.last_page || 1
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to fetch attendance data');
    }
  };

  const fetchDropdowns = async () => {
    // Dropdowns are now handled by PaginatedSelect, so we don't need to prefetch all data.
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/visit-sessions/visit-summary');
      if (response.data && response.data.data) {
        setSummary(response.data.data);
      } else if (response.data) {
        setSummary(response.data);
      }
    } catch (error) {
      console.warn('Attendance summary not available:', error.message);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    fetchSummary();
  }, []);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current_page: 1 }));
  }, [activeTab, searchTerm, filters]);

  useEffect(() => {
    fetchAttendance();
  }, [pagination.current_page, pagination.per_page, activeTab, searchTerm, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const handleAddClick = () => {
    setEditingRecord(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setIsDrawerOpen(true);
    setOpenMenuIndex(null);
  };

  const handleRecalculate = async (record) => {
    setOpenMenuIndex(null);
    try {
      await api.post('/attendance/recalculate', {
        employee_id: record.employee_id || record.employee?.id,
        attendance_date: record.visit_date || record.attendance_date
      });
      toast.success('Attendance recalculated successfully');
      fetchAttendance();
      fetchSummary();
    } catch (error) {
      console.error('Error recalculating attendance:', error);
      toast.error('Failed to recalculate attendance');
    }
  };

  const handleDelete = async (record) => {
    setOpenMenuIndex(null);
    MySwal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete attendance for "${record.employee?.employee_name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/visit-sessions/${record.id}`, {
            data: { employee_id: JSON.parse(localStorage.getItem('user') || '{}').id }
          });
          toast.success('Record deleted successfully');
          fetchAttendance();
          fetchSummary();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete record');
        }
      }
    });
  };

  const handlePunchOut = async (record) => {
    setOpenMenuIndex(null);
    const { value: time } = await MySwal.fire({
      title: '<span style="font-family: \'Outfit\', sans-serif; font-weight: 800;">Manual Punch Out</span>',
      html: `
        <div style="text-align: left; font-family: 'Outfit', sans-serif;">
          <label style="display: block; margin-bottom: 12px; font-weight: 600; color: #475569; font-size: 14px;">Select Punch Out Time</label>
          <div style="position: relative;">
            <input type="time" id="punch-out-time" class="swal2-input" 
              style="width: 100%; margin: 0; border-radius: 12px; border: 2px solid #e2e8f0; padding: 12px; font-family: 'Outfit', sans-serif; font-weight: 600;" 
              value="${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}">
          </div>
          <p style="margin-top: 12px; color: #94a3b8; font-size: 12px;">The checkout will be recorded for <b>${record.visit_date}</b></p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirm Checkout',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#64748b',
      customClass: {
        popup: styles['swal-premium-popup'],
        confirmButton: styles['swal-confirm-btn'],
        cancelButton: styles['swal-cancel-btn']
      },
      buttonsStyling: false,
      preConfirm: () => {
        return document.getElementById('punch-out-time').value;
      }
    });

    if (time) {
      try {
        const dateTime = `${record.visit_date} ${time}`;
        await api.post('/visit-sessions/manual-checkout', {
          session_id: record.id,
          employee_id: record.employee?.id || record.employee_id,
          project_id: record.project?.id || record.project_id,
          punch_out_time: dateTime
        });
        toast.success('Punched out successfully');
        fetchAttendance();
        fetchSummary();
      } catch (error) {
        console.error('Error punching out:', error);
        toast.error(error.response?.data?.message || 'Failed to punch out');
      }
    }
  };

  const handleApprovalAction = async (record, action) => {
    setOpenMenuIndex(null);
    try {
      // Assuming you have an API to approve/reject
      const statusToSet = action === 'approve' ? 'FULL_DAY' : 'ABSENT'; // Update based on actual backend logic
      await api.post('/attendance/override', {
        employee_id: record.employee_id,
        attendance_date: record.attendance_date ? record.attendance_date.split('T')[0] : null,
        status: statusToSet,
        reason: `${action === 'approve' ? 'Approved' : 'Rejected'} by Admin`,
        admin_id: JSON.parse(localStorage.getItem('user') || '{}').id
      });
      toast.success(`Request ${action}d successfully`);
      fetchAttendance();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${action} request`);
    }
  };

  const getStatusClass = (status) => {
    if (!status) return styles['status-default'];
    switch (status.toUpperCase()) {
      case 'PRESENT':
      case 'FULL_DAY':
        return styles['status-present'];
      case 'HALF_DAY':
      case 'FIRST_HALF':
      case 'SECOND_HALF':
        return styles['status-halfday'];
      case 'ABSENT':
        return styles['status-absent'];
      case 'HOLIDAY':
        return styles['status-holiday'];
      case 'HOLIDAY_WORKING':
        return styles['status-holiday-working'];
      case 'LEAVE':
        return styles['status-leave'];
      case 'LEAVE_WORKING':
        return styles['status-leave-working'];
      case 'IN_PROGRESS':
        return styles['status-in-progress'];
      case 'PENDING_ADMIN_APPROVAL':
        return styles['status-pending'];
      default:
        return styles['status-default'];
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) {
      return `${h}h ${m}m`;
    }
    return `${m}m`;
  };

  const hasActiveFilters = filters.status || filters.department || filters.date;

  return (
    <div className={`${styles['attendance-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Attendance', path: '/employees/attendance' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Attendance Tracking</h1>
              <p>Monitor daily employee presence, working hours, and OT across sites.</p>
            </div>
            <button className={styles['add-btn']} onClick={handleAddClick}>
              <Plus size={20} />
              <span>Manual Attendance</span>
            </button>
          </div>


          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Attendance Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {summary.present_today} Present Today
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                  {m.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search employees or sites..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' }
                  ]}
                  onChange={(opt) => setPagination(prev => ({ ...prev, per_page: opt.value }))}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Attendance</h3>
                  <p>Drill down by status, department or date.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    placeholder="All Status"
                    options={statusOptions}
                    value={filters.status}
                    onChange={(opt) => setFilters(prev => ({ ...prev, status: opt }))}
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Employee</label>
                  <PaginatedSelect
                    endpoint="/employees-dropdown"
                    valueKey="id"
                    labelKey="employee_name"
                    placeholder="All Employees"
                    value={filters.employee?.value}
                    onChange={(val, opt) => setFilters(prev => ({ ...prev, employee: opt ? { value: opt.id, label: opt.employee_name } : null }))}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Project Site</label>
                  <PaginatedSelect
                    endpoint="/projects-dropdown"
                    valueKey="id"
                    labelKey="project_name"
                    placeholder="All Projects"
                    value={filters.project?.value}
                    onChange={(val, opt) => setFilters(prev => ({ ...prev, project: opt ? { value: opt.id, label: opt.project_name } : null }))}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Date</label>
                  <input
                    type="date"
                    className={styles['filter-date-input']}
                    value={filters.date}
                    onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={() => setFilters({ status: null, department: null, date: '' })}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['tabs-wrapper']}>
              <button
                className={`${styles['tab-btn']} ${activeTab === 'all' ? styles['active-tab'] : ''}`}
                onClick={() => setActiveTab('all')}
              >
                <LayoutDashboard size={18} />
                All Records
              </button>
              <button
                className={`${styles['tab-btn']} ${activeTab === 'pending' ? styles['active-tab'] : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                <AlertCircle size={18} />
                Pending Approvals
              </button>
            </div>

            <div className={styles['table-wrapper']}>
              <table className={styles['attendance-table']}>
                <thead>
                  <tr>
                    <th>EMPLOYEE & SITE</th>
                    <th>DATE</th>
                    <th>IN / OUT</th>
                    <th>HOURS</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.length > 0 ? (
                    attendance.map((record) => {
                      const isPendingTab = activeTab === 'pending';
                      const recordDate = isPendingTab ? record.attendance_date : record.visit_date;
                      const punchInTime = isPendingTab ? record.first_punch_in : record.punch_in;
                      const punchOutTime = isPendingTab ? record.last_punch_out : record.punch_out;
                      const durationMins = isPendingTab ? record.total_work_minutes : record.duration_minutes;
                      const statusVal = isPendingTab ? record.status : (record?.attendance?.status || record?.session_status);
                      const projectName = isPendingTab ? 'Multiple Sites' : (record.project?.name || 'N/A');
                      const empName = record.employee?.name || record.employee?.employee_name || 'N/A';

                      return (
                        <tr key={record.id}>
                          <td className={styles['name-cell']}>
                            <div className={styles['user-info']}>
                              <div className={styles['avatar']}>
                                {empName.charAt(0)}
                              </div>
                              <div className={styles['emp-site-info']}>
                                <span
                                  className={`${styles['name-text']} ${styles['clickable']}`}
                                  onClick={() => navigate(`/employees/attendance/view/${record.employee_id || record.employee?.id}`)}
                                >
                                  {empName}
                                </span>
                                <span className={styles['site-text']}><MapPin size={12} /> {projectName}</span>
                              </div>
                            </div>
                          </td>
                          <td className={styles['date-cell']}>{formatDate(recordDate)}</td>
                          <td className={styles['time-cell']}>
                            <div className={styles['punch-times']}>
                              <span className={styles['in']}>{punchInTime ? new Date(punchInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span>
                              <span className={styles['out']}>{punchOutTime ? new Date(punchOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span>
                            </div>
                          </td>
                          <td className={styles['hours-cell']}>
                            <div className={styles['hours-badge']}>
                              <Clock9 size={12} /> {formatDuration(durationMins)}
                            </div>
                          </td>
                          <td>
                            <div className={styles['status-with-override']}>
                              <span className={`${styles['status-badge']} ${getStatusClass(statusVal)}`}>
                                {formatStatus(statusVal)}
                              </span>
                            </div>
                          </td>
                          <td className={styles['actions-cell']}>
                            <div className={`action-wrapper ${styles['action-wrapper']}`}>
                              <button
                                className={`${styles['action-btn']} ${openMenuIndex === record.id ? styles['active'] : ''}`}
                                onClick={(e) => toggleActionMenu(record.id, e)}
                              >
                                <MoreVertical size={18} />
                              </button>

                              {openMenuIndex === record.id && (
                                <div
                                  className={styles['action-dropdown']}
                                  style={{ top: menuPosition.top, left: menuPosition.left }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/attendance/view/${record.employee_id || record.employee?.id}`)}>
                                    <Eye size={16} /> View
                                  </button>
                                  {activeTab === 'pending' ? (
                                    <>
                                      <button className={styles['dropdown-item']} onClick={() => handleApprovalAction(record, 'approve')} style={{ color: '#10b981' }}>
                                        <CheckCircle2 size={16} /> Approve
                                      </button>
                                      <button className={styles['dropdown-item']} onClick={() => handleApprovalAction(record, 'reject')} style={{ color: '#ef4444' }}>
                                        <XCircle size={16} /> Reject
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button className={styles['dropdown-item']} onClick={() => handleEditClick(record)}>
                                        <Pencil size={16} /> Edit
                                      </button>
                                      {!punchOutTime && (
                                        <button className={styles['dropdown-item']} onClick={() => handlePunchOut(record)}>
                                          <Clock size={16} /> Punch Out
                                        </button>
                                      )}
                                      <button className={styles['dropdown-item']} onClick={() => handleRecalculate(record)}>
                                        <RefreshCw size={16} /> Recalculate
                                      </button>
                                      <div className={styles['dropdown-divider']}></div>
                                      <button
                                        className={`${styles['dropdown-item']} ${styles['deselect']}`}
                                        onClick={() => handleDelete(record)}
                                      >
                                        <Trash2 size={16} /> Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : !loading && (
                    <NoData message="No records found." isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['attendance-cards']}>
              {attendance.length > 0 ? (
                attendance.map((record) => {
                  const isPendingTab = activeTab === 'pending';
                  const recordDate = isPendingTab ? record.attendance_date : record.visit_date;
                  const punchInTime = isPendingTab ? record.first_punch_in : record.punch_in;
                  const punchOutTime = isPendingTab ? record.last_punch_out : record.punch_out;
                  const durationMins = isPendingTab ? record.total_work_minutes : record.duration_minutes;
                  const statusVal = isPendingTab ? record.status : (record?.attendance?.status || record?.session_status);
                  const projectName = isPendingTab ? 'Multiple Sites' : (record.project?.name || record.project?.project_name || 'N/A');
                  const empName = record.employee?.name || record.employee?.employee_name || 'N/A';

                  return (
                    <div key={record.id} className={styles['attendance-card']}>
                      <div className={styles['card-header']}>
                        <div className={styles['card-user']}>
                          <div className={styles['avatar']}>{empName.charAt(0)}</div>
                          <div className={styles['card-emp-info']}>
                            <span 
                              className={`${styles['card-name']} ${styles['clickable']}`}
                              onClick={() => navigate(`/employees/attendance/view/${record.employee_id || record.employee?.id}`)}
                            >
                              {empName}
                            </span>
                            <span className={styles['card-site']}><MapPin size={12} /> {projectName}</span>
                          </div>
                        </div>
                        <span className={`${styles['status-badge']} ${getStatusClass(statusVal)}`}>
                          {formatStatus(statusVal)}
                        </span>
                      </div>

                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Date</span>
                          <span className={styles['detail-value']}>{formatDate(recordDate)}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Working Hours</span>
                          <span className={styles['detail-value']}>
                            <Clock9 size={12} style={{ display: 'inline', marginRight: '4px' }} />
                            {formatDuration(durationMins)}
                          </span>
                        </div>
                        <div className={`${styles['detail-item']} ${styles['full-width-detail']}`}>
                          <span className={styles['detail-label']}>Punch Times</span>
                          <div className={styles['punch-times-card']}>
                            <span className={styles['in']}>In: {punchInTime ? new Date(punchInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span>
                            <span className={styles['out']}>Out: {punchOutTime ? new Date(punchOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</span>
                          </div>
                        </div>
                      </div>

                      <div className={styles['card-footer']}>
                        {!punchOutTime && !isPendingTab && (
                          <button 
                            className={styles['card-action-btn']} 
                            onClick={() => handlePunchOut(record)} 
                            style={{ color: '#f97316', borderColor: '#fed7aa', backgroundColor: '#fff7ed' }}
                          >
                            <Clock size={14} /> Punch Out
                          </button>
                        )}

                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === record.id ? styles['active'] : ''}`}
                            onClick={(e) => toggleActionMenu(record.id, e)}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenuIndex === record.id && (
                            <div
                              className={styles['action-dropdown']}
                              style={{ top: menuPosition.top, left: menuPosition.left, position: 'fixed' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button className={styles['dropdown-item']} onClick={() => { setOpenMenuIndex(null); navigate(`/employees/attendance/view/${record.employee_id || record.employee?.id}`); }}>
                                <Eye size={16} /> View
                              </button>
                              {isPendingTab ? (
                                <>
                                  <button className={styles['dropdown-item']} onClick={() => handleApprovalAction(record, 'approve')} style={{ color: '#10b981' }}>
                                    <CheckCircle2 size={16} /> Approve
                                  </button>
                                  <button className={styles['dropdown-item']} onClick={() => handleApprovalAction(record, 'reject')} style={{ color: '#ef4444' }}>
                                    <XCircle size={16} /> Reject
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button className={styles['dropdown-item']} onClick={() => handleEditClick(record)}>
                                    <Pencil size={16} /> Edit
                                  </button>
                                  <button className={styles['dropdown-item']} onClick={() => handleRecalculate(record)}>
                                    <RefreshCw size={16} /> Recalculate
                                  </button>
                                  <div className={styles['dropdown-divider']}></div>
                                  <button
                                    className={`${styles['dropdown-item']} ${styles['deselect']}`}
                                    onClick={() => handleDelete(record)}
                                  >
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : !loading && (
                <NoData message="No records found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            />
          </div>

          <Footer />
        </main>
      </div>

      <AttendanceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          fetchAttendance();
          fetchSummary();
        }}
        record={editingRecord}
      />
    </div>
  );
};

export default Attendance;
